/**
 * The Height breach report's PDF in Teslyn Cloud (MB's decision 1034, Q900 A; R219; 20261104237000_breach_report_pdf.sql):
 * each time a Teslyn Field laptop writes the report it sends the PDF at the next sync; Teslyn Cloud keeps the latest
 * one per project. "Open breach report" opens it in the document viewer (R235) on Teslyn Cloud's own address: in the
 * Safety register for the project's staff, and on the client portal's Safety register page and in Reports (R234) for a
 * client while at least one of the project's breaches is approved for them (the database decides,
 * `breach_report_pdf` and the bucket's policy).
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import { BREACH_REPORT_BUCKET } from '@teslyn/domain';
import { missingView, plainPortalError } from '../portal/api.ts';
import { loadClientSafetyRegister } from '../safety/client-register-api.ts';

/** Where the project's latest report PDF is. */
export interface BreachReportPdf {
  readonly bucket: string;
  readonly object_path: string;
  readonly file_name: string;
  readonly size_bytes: number;
  readonly uploaded_at: string;
}

/**
 * The project's latest report PDF, or null: none sent yet, not the caller's to read, or Teslyn Cloud without the
 * update (20261104237000 not applied).
 */
export async function loadBreachReportPdf(
  sb: SupabaseClient,
  projectId: string,
): Promise<BreachReportPdf | null> {
  const { data, error } = (await sb.rpc('breach_report_pdf', { p_project: projectId })) as {
    data: unknown;
    error: { code?: string; message: string } | null;
  };
  if (missingView(error) || /breach_report_pdf/.test(error?.message ?? '')) return null;
  if (error) throw new Error(plainPortalError(error));
  const r = data as Partial<BreachReportPdf> | null;
  return r && typeof r.object_path === 'string' && r.object_path
    ? {
        bucket: BREACH_REPORT_BUCKET,
        object_path: r.object_path,
        file_name: r.file_name ?? '',
        size_bytes: Number(r.size_bytes ?? 0),
        uploaded_at: r.uploaded_at ?? '',
      }
    : null;
}

/** The breach report's row in Reports (R234): the project's latest PDF, and whether its client can open it now. */
export interface BreachReportRow extends BreachReportPdf {
  readonly projectId: string;
  /** Is a live breach of the project approved for the client? Null when it could not be read. */
  readonly approved: boolean | null;
}

/** Staff read the choices as the Safety register does (a client cannot read them). */
async function breachApproved(sb: SupabaseClient, projectId: string): Promise<boolean | null> {
  const shown = (await sb
    .from('safety_register_client')
    .select('record_id')
    .eq('project_id', projectId)
    .eq('source', 'height_breach')
    .eq('shown', true)) as { data: { record_id: string }[] | null; error: unknown };
  if (shown.error || !shown.data) return null;
  if (shown.data.length === 0) return false;
  const live = (await sb
    .from('height_breach')
    .select('id')
    .in(
      'id',
      shown.data.map((r) => r.record_id),
    )
    .is('deleted_at', null)) as { data: unknown[] | null; error: unknown };
  return live.error || !live.data ? null : live.data.length > 0;
}

/**
 * The breach report rows of the given projects. For a client `breach_report_pdf` already decides, but it gives staff
 * the PDF whatever was approved, so a client view also asks the Safety register that view shows: Preview as client
 * then lists the row exactly when the client would have it.
 */
export async function loadBreachReportRows(
  sb: SupabaseClient,
  projects: readonly { readonly id: string; readonly clientOrgId: string | null }[],
  mode: 'client' | 'staff',
): Promise<BreachReportRow[]> {
  const rows = await Promise.all(
    projects.map(async (p): Promise<BreachReportRow | null> => {
      const pdf = await loadBreachReportPdf(sb, p.id);
      if (!pdf) return null;
      if (mode === 'staff')
        return { ...pdf, projectId: p.id, approved: await breachApproved(sb, p.id).catch(() => null) };
      if (!p.clientOrgId) return null;
      const register = await loadClientSafetyRegister(sb, p.clientOrgId);
      return register.some((r) => r.project_id === p.id && r.from_breach)
        ? { ...pdf, projectId: p.id, approved: true }
        : null;
    }),
  );
  return rows.filter((r): r is BreachReportRow => r !== null);
}
